import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface SeriesImportStatus {
  canImport: boolean;
  alreadyImported: boolean;
  importsUsed: number;
  importsRemaining: number;
  resetsAt: Date | null;
  loading: boolean;
}

export function useSeriesImportStatus(seriesId: string | undefined): SeriesImportStatus {
  const [status, setStatus] = useState<SeriesImportStatus>({
    canImport: false,
    alreadyImported: false,
    importsUsed: 0,
    importsRemaining: 0,
    resetsAt: null,
    loading: true,
  });

  useEffect(() => {
    if (!seriesId) {
      setStatus(s => ({ ...s, loading: false }));
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !mounted) return;

        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

        const [countResult, settingResult, existingResult, oldestResult] = await Promise.all([
          supabase.rpc('get_user_series_import_count'),
          supabase.from('series_import_settings').select('value').eq('key', 'monthly_import_limit').single(),
          supabase.from('series_import_log')
            .select('id')
            .eq('user_id', user.id)
            .eq('series_id', seriesId)
            .gte('imported_at', thirtyDaysAgo)
            .maybeSingle(),
          supabase.from('series_import_log')
            .select('imported_at')
            .eq('user_id', user.id)
            .gte('imported_at', thirtyDaysAgo)
            .order('imported_at', { ascending: true })
            .limit(1)
            .maybeSingle(),
        ]);

        if (!mounted) return;

        const importsUsed: number = countResult.data ?? 0;
        const limit = parseInt(settingResult.data?.value ?? '2');
        const alreadyImported = !!existingResult.data;
        const importsRemaining = Math.max(0, limit - importsUsed);

        let resetsAt: Date | null = null;
        if (oldestResult.data?.imported_at) {
          resetsAt = new Date(new Date(oldestResult.data.imported_at).getTime() + 30 * 24 * 60 * 60 * 1000);
        }

        setStatus({
          canImport: !alreadyImported && importsRemaining > 0,
          alreadyImported,
          importsUsed,
          importsRemaining,
          resetsAt,
          loading: false,
        });
      } catch {
        if (mounted) setStatus(s => ({ ...s, loading: false }));
      }
    };

    load();
    return () => { mounted = false; };
  }, [seriesId]);

  return status;
}
